import React, { useCallback, useMemo, useRef } from 'react';
import { Button, ButtonProps, CircularProgress, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTranslate } from 'hooks/use-translate';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import { useSourceNotebooksAll } from 'components/hooks';
import { useOpen } from 'AurionCR/components/hooks';
import { useOpenValue } from 'hooks';
import { getNotebookFormComponent, withPrescriptionTask } from 'components/notebook-form-generator';
import BookmarksIcon from '@mui/icons-material/Bookmarks';

interface Props extends ButtonProps {
  userPatientProfileID: string;
  onDone?: () => void;
}

export const ButtonAddNotebook: React.FC<React.PropsWithChildren<Props>> = ({
  userPatientProfileID,
  onDone,
  ...rest
}) => {
  const sourceNotebooks = useSourceNotebooksAll();
  const { t } = useTranslate();
  // Phase F3 (F3-03, D-69): below md the card's action bar has no room for labels; the icon keeps the title (F1's add-task pattern).
  const isBelowMd = useIsBelowMd();
  const ref = useRef<HTMLButtonElement>(null);
  const { isOpen, handleOpen, handleClose } = useOpen();

  const { openValue, onOpen: onOpenDialog, onClose: onCloseDialog } = useOpenValue<string>();

  const factoryClickMenuItem = useCallback(
    (id: string) => {
      return () => {
        onOpenDialog(id);
        handleClose();
      };
    },
    [handleClose, onOpenDialog],
  );

  const notebook = useMemo(() => {
    return sourceNotebooks.data.find((note) => note.id === openValue);
  }, [sourceNotebooks.data, openValue]);

  const FormGenerator = useMemo(() => {
    return notebook ? withPrescriptionTask(getNotebookFormComponent(notebook)) : null;
  }, [notebook]);

  const icon = sourceNotebooks.loading ? <CircularProgress size={20} /> : <BookmarksIcon />;

  return (
    <>
      <Tooltip title={t('add-notebook-tooltip')}>
        <Button
          onClick={handleOpen}
          ref={ref}
          variant={'contained'}
          color={'primary'}
          startIcon={isBelowMd ? undefined : icon}
          // the Tooltip above carries the hint; a native title would double it
          aria-label={t('add-notebook')}
          disabled={sourceNotebooks.loading || sourceNotebooks.data.length === 0}
          {...rest}
        >
          {isBelowMd ? icon : t('add-notebook')}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={ref.current}
        keepMounted
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {sourceNotebooks.data.map((item) => (
          <MenuItem key={item.id} onClick={factoryClickMenuItem(item.id)}>
            {item.title}
          </MenuItem>
        ))}
      </Menu>
      {openValue && FormGenerator && (
        <FormGenerator
          notebookID={openValue}
          userPatientProfileID={userPatientProfileID}
          onClose={onCloseDialog}
          onDone={onCloseDialog}
          onCreated={onDone}
          meetingFromDateTime={undefined}
          meetingToDateTime={undefined}
        />
      )}
    </>
  );
};
